
import {
  Routes,
  Route,
} from "react-router-dom";
import React, { Suspense, lazy, useEffect } from "react"; // Add useEffect
import { Toaster } from "sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import LoadingIndicator from "@/components/LoadingIndicator";
import { ErrorBoundary } from "react-error-boundary";
import NavigationMonitor from "@/components/navigation/NavigationMonitor";
import NotFound from "@/pages/NotFound";
import DiagnosticsView from "@/components/diagnostics/DiagnosticsView";
import PagePerformance from "@/components/PagePerformance";
import DiagnosticsInitializer from "@/components/diagnostics/DiagnosticsInitializer";

// Lazy load page components for better performance
const Index = lazy(() => import("@/pages/Index"));
const Auth = lazy(() => import("@/pages/Auth"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const ToolsCategory = lazy(() => import("@/pages/ToolsCategory"));
const Community = lazy(() => import("@/pages/Community"));
const DigitalMarketing = lazy(() => import("@/pages/DigitalMarketing"));
const ContentCreation = lazy(() => import("@/pages/ContentCreation"));
const Profile = lazy(() => import("@/pages/Profile"));
const ContentManagement = lazy(() => import("@/pages/ContentManagement"));
const Admin = lazy(() => import("@/pages/Admin"));

// Loading fallback component
const PageLoading = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#00A300]"></div>
  </div>
);

// Global error fallback component
const ErrorFallback = ({ error, resetErrorBoundary }: { error: Error, resetErrorBoundary: () => void }) => {
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-center p-4">
      <h2 className="text-red-600 text-xl font-bold mb-4">Something went wrong</h2>
      <pre className="bg-gray-100 p-4 rounded max-w-lg overflow-auto text-left mb-4 text-xs">
        {error.message}
      </pre>
      <button 
        onClick={() => {
          // Clear any stuck loading indicators
          if (window._loadingIndicator) {
            window._loadingIndicator.endAllRequests();
          }
          resetErrorBoundary();
        }} 
        className="bg-[#00A300] hover:bg-[#008000] text-white px-4 py-2 rounded"
      >
        Try again
      </button>
    </div>
  );
};

const App = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <DiagnosticsInitializer />
      <Toaster position="top-right" richColors closeButton />
      <LoadingIndicator />
      <NavigationMonitor />
      <DiagnosticsView />
      <PagePerformance />
      <Routes>
        <Route path="/" element={
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<PageLoading />}>
              <Index />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/auth" element={
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<PageLoading />}>
              <Auth />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Suspense fallback={<PageLoading />}>
              <Dashboard />
            </Suspense>
          </ProtectedRoute>
        } />
        <Route path="/tools/:categoryId" element={
          <ProtectedRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageLoading />}>
                <ToolsCategory />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/community" element={
          <ProtectedRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageLoading />}>
                <Community />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageLoading />}>
                <Profile />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/content" element={
          <ProtectedRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageLoading />}>
                <ContentManagement />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        <Route path="/digital-marketing" element={
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<PageLoading />}>
              <DigitalMarketing />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/content-creation" element={
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <Suspense fallback={<PageLoading />}>
              <ContentCreation />
            </Suspense>
          </ErrorBoundary>
        } />
        <Route path="/admin" element={
          <ProtectedRoute>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <Suspense fallback={<PageLoading />}>
                <Admin />
              </Suspense>
            </ErrorBoundary>
          </ProtectedRoute>
        } />
        {/* Add 404 route to catch all unmatched routes */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
};

export default App;
